// Сведение карточек-дублей судов: форма ответа ядра и разбор её на клиенте.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ ТИПОВ. Тот же приём, что у lib/dq.ts: типы отдельно от запроса,
// потому что по ним считается почти всё, что видно на экране (порядок групп, отбор,
// подписи), и это надо проверять тестом без сети.
//
// ГЛАВНОЕ, ЧТО ЭТИ ТИПЫ ОБЯЗАНЫ РАЗЛИЧАТЬ: «человек назвал главную карточку» и «главную
// предположили мы». Это разные вещи, и если их сложить в одно поле `main_id`, экран
// начнёт выдавать догадку за решение. Поэтому источник главной приезжает отдельным
// полем `main_from` и на экране показывается словами, а не цветом.

/** Откуда взялась главная карточка группы. Порядок — по убыванию надёжности. */
export type ОткудаГлавная =
  /** В названии лишней карточки написано `[ДУБЛЬ→#N]`, и N — существующая карточка группы. */
  | 'человек'
  /** Пометка «дубль» есть, но номера главной в ней нет, и непомеченная карточка ровно одна. */
  | 'исключение'
  /** Пометок нет вовсе. Главная выбрана правилом ядра — это ДОГАДКА. */
  | 'догадка'

/** Что стало с полем при сравнении карточек группы. */
export type Вердикт =
  /** Значения заполнены и различаются. Автоматом сводить нельзя. */
  | 'конфликт'
  /** У главной пусто, у лишней заполнено. При слиянии Bitrix это потеряет. */
  | 'дополняет'
  /** Заполнено одинаково либо пусто у всех. */
  | 'совпадает'

/** Карточка судна внутри группы. */
export interface Карточка {
  id: number
  name: string
  /** Номер главной из пометки `[ДУБЛЬ→#N]`, если человек его написал. */
  marked_to: number | null
  /** Пометка «дубль» есть, а номера в ней нет. */
  marked_blank: boolean
  is_main: boolean
  created: string | null
  modified: string | null
  /** Сколько объектов ссылается на эту карточку. */
  links: number
  open_deals: number
}

/** Строка сравнения полей: что у главной, что у лишних, и что из этого следует. */
export interface Поле {
  key: string
  title: string
  main_value: string | null
  /** Значения тех же полей у лишних карточек. */
  others: { card_id: number; value: string | null }[]
  verdict: Вердикт
  /**
   * Различие мусорное: '0', '000000000', пустая строка в одной из карточек. Показывается
   * отдельно, потому что мусорный «конфликт» не должен пугать наравне с настоящим.
   */
  junk: boolean
}

/** Один объект, висящий на лишней карточке: его надо перевесить руками в Bitrix. */
export interface Ссылка {
  /** Код вида: 'deal', 'smart:170', 'p73:vessel' и т. п. */
  kind: string
  /** Человеческое название вида: «Сделка», «АВР», «Отчёт по сделке», «Счёт клиенту». */
  kind_title: string
  /** Карточка-дубль, на которой объект висит сейчас. */
  card_id: number
  id: number | string
  title: string | null
  date: string | null
  amount: number | null
  status: string | null
  /** Готовый адрес для открытия. null — открыть нечем (запись чужой базы без своей страницы). */
  url: string | null
  /** Ссылку синк не перевесит: она живёт в чужой базе и правится владельцем источника. */
  manual_only: boolean
}

/** Живой бизнес-процесс на карточке или на висящем на ней объекте. */
export interface Процесс {
  card_id: number
  template: string
  /** Незавершённых экземпляров. */
  instances: number
  /** Незакрытых заданий живым людям. */
  tasks: number
  /** На чём висит: 'contact' — на самой карточке судна, 'deal' — на сделке и т. д. */
  on: string
}

/** Строка списка групп. */
export interface Группа {
  imo: string
  /** Название главной карточки. */
  title: string
  cards: number
  /** Лишних карточек в группе (cards - 1). */
  extra: number
  links: number
  open_deals: number
  /** Ссылки размазаны более чем по одной карточке — слияние требует перевешивания. */
  spread: boolean
  main_id: number
  main_from: ОткудаГлавная
  /** Почему предложена именно эта карточка. Пусто, когда главную назвал человек. */
  main_why: string | null
  conflict: boolean
  complement: boolean
  /** Полей в прямом конфликте. */
  conflict_fields: number
  /** Полей, которые потеряются при слиянии. */
  lost_fields: number
  bp_live: number
  bp_tasks: number
  /** Ссылок на самой главной карточке — их перевешивать не надо, они остаются на месте. */
  main_links: number
  /**
   * На лишней карточке ссылок БОЛЬШЕ, чем на главной. Не повод менять главную молча —
   * там, где её назвал человек, его решение важнее экономии кликов, — но человек должен
   * увидеть это до начала: работы в такой группе максимум.
   */
  heavier_extra: boolean
  /** Какие именно поля личности разошлись: «MMSI», «Флаг судна», «Порт приписки». */
  conflict_on: string[]
}

export interface ОтветГрупп {
  rows: Группа[]
  total: number
  next: number | null
}

export interface ОтветГруппы {
  imo: string
  main_id: number
  main_from: ОткудаГлавная
  main_why: string | null
  cards: Карточка[]
  fields: Поле[]
  links: Ссылка[]
  bp: Процесс[]
}

/** Числа, которые легко перепутать. Стоят отдельно от `totals` и подписаны на экране. */
export interface Подробно {
  /** Из скольких конфликтующих групп расходятся именно номером MMSI. */
  conflict_mmsi: number
  lost_identity_groups: number
  heavier_extra_groups: number
  /** По ВСЕМ полям карточки, а не по трём полям личности судна. Другой вопрос. */
  conflict_all_fields: number
  lost_all_fields: number
  links_by_kind: Record<string, number>
}

export interface Свод {
  groups: number
  cards: number
  extra: number
  marked_cards: number
  marked_groups: number
  guess_groups: number
  links: number
  open_deals: number
  spread_groups: number
  conflict_groups: number
  complement_groups: number
  orphan_deals: number
}

export interface ОтветСправочника {
  totals: Свод
  detail: Подробно
  /** Порядок действий словами. Ядро отдаёт его вместе с данными, а не экран рисует. */
  steps: string[]
  /** Чего этот экран не видит и что синк не перевесит. */
  notes: string[]
  kinds: { code: string; title: string }[]
  counted_at: string
  /** Возраст кэша ядра в секундах. Показан на экране: молчать о нём — тихое враньё. */
  cached_sec: number
}

// ───────────────────────────── разбор на клиенте ─────────────────────────────
//
// Фильтры и порядок считаются здесь, а не в ядре: групп 357 (на 28.07.2026), они приезжают
// одной страницей, и ходить в сеть за каждым переключением было бы тратой. Ровно тот же
// приём, что в lib/dq.отобрать.

export type Порядок = 'вес' | 'сделки' | 'ссылки' | 'imo'
export type Отбор = 'все' | 'человек' | 'догадка' | 'конфликт' | 'процесс'

/** Надёжность выбора главной словами — одна формулировка на весь экран. */
export function откудаГлавная(о: ОткудаГлавная): string {
  if (о === 'человек') return 'главную назвал человек'
  if (о === 'исключение') return 'человек пометил дубль, главная — единственная непомеченная'
  return 'главную предположили мы — это догадка, а не решение'
}

/**
 * Вес группы: во сколько обойдётся ручное сведение. Открытая сделка дороже прочей ссылки
 * в десять раз не «для красоты» — за ней стоит работа, которая идёт прямо сейчас, и
 * потерять её ссылку значит остановить работу, а не испортить историю.
 */
export function вес(г: Группа): number {
  return г.open_deals * 10 + г.links
}

export function отобрать(строки: Группа[], отбор: Отбор, поиск: string): Группа[] {
  const q = поиск.trim().toLowerCase()
  return строки.filter((г) => {
    if (отбор === 'человек' && г.main_from === 'догадка') return false
    if (отбор === 'догадка' && г.main_from !== 'догадка') return false
    if (отбор === 'конфликт' && !г.conflict) return false
    if (отбор === 'процесс' && г.bp_live === 0 && г.bp_tasks === 0) return false
    if (!q) return true
    return г.imo.includes(q) || (г.title ?? '').toLowerCase().includes(q)
  })
}

export function упорядочить(строки: Группа[], порядок: Порядок): Группа[] {
  const к = [...строки]
  if (порядок === 'imo') return к.sort((a, b) => a.imo.localeCompare(b.imo))
  if (порядок === 'сделки') return к.sort((a, b) => b.open_deals - a.open_deals || вес(b) - вес(a))
  if (порядок === 'ссылки') return к.sort((a, b) => b.links - a.links || вес(b) - вес(a))
  return к.sort((a, b) => вес(b) - вес(a) || a.imo.localeCompare(b.imo))
}
